import React from 'react';
import type { GetServerSideProps } from 'next';
import Helmet from 'react-helmet';

import { receiveUser } from 'actions/UserActions';
import { initializeStore } from 'src/store/nextStore';
import { fetchUser } from 'api/server/users';

import { ssrGetThemeV2GroupByThemeKey } from 'pages/WebsiteDesignPdp/api/server/websitePdpSSRApiHelpers';
import { receiveWebsitePdpThemeGroup } from 'pages/WebsiteDesignPdp/reducer/websitePdpActions';
import WebsiteDesignPdp from 'pages/WebsiteDesignPdp';

import Logger from '@zola-helpers/server/dist/es/logger';
import { Device } from 'lib/universal/utils/devices';

export type PreAuthWebsitePdpContainerProps = {
  isDesktop: boolean;
  isWebsiteV2Preauth: boolean;
  showTopBanner?: boolean;
  themeKey: string;
};
const PreAuthWebsitePdpContainer: React.FC<PreAuthWebsitePdpContainerProps> = ({
  isDesktop,
  showTopBanner,
  themeKey,
}) => (
  <>
    <Helmet meta={[{ name: 'robots', content: 'noindex, nofollow' }]} />
    <WebsiteDesignPdp isDesktop={isDesktop} showTopBanner={showTopBanner} themeKey={themeKey} />
  </>
);

export const getServerSideProps: GetServerSideProps = async ({ req, res, query }) => {
  if (!query?.themeKey || typeof query?.themeKey !== 'string') {
    res.writeHead(302, {
      Location: '/wedding-planning/website/designs',
    });
    res.end();
    return { props: {} };
  }

  const userContext = await fetchUser(req);
  const themeKey = query.themeKey.toLowerCase();

  if (userContext?.has_completed_onboarding || !userContext) {
    // Redirect to the post-auth designs PDP
    res.writeHead(302, {
      Location: `/wedding/manage/website/design/${themeKey}`,
    });
    res.end();
    return { props: {} };
  }
  const { referer } = req.headers;
  const showTopBanner = Boolean(!referer?.includes('zola.com'));

  const reduxStore = initializeStore();
  const { dispatch, getState } = reduxStore;
  dispatch(receiveUser(userContext));

  const themeGroup = await ssrGetThemeV2GroupByThemeKey(themeKey)
    .then(themeGroupData => {
      dispatch(receiveWebsitePdpThemeGroup(themeGroupData));
      return themeGroupData;
    })
    .catch((e: Error) => {
      Logger.warn(
        `Could not fetch themeGroup for themeKey[${themeKey}] on pre-auth theme PDP. ${e}`
      );
    });

  const themeGroupTheme = themeGroup?.themes?.[0];
  if (!themeGroupTheme) {
    res.writeHead(302, {
      Location: '/wedding-planning/website/designs',
    });
    res.end();
    return { props: {} }; // stop execution
  }

  const userAgent = req.headers['user-agent'];
  const device = new Device(userAgent);
  const initialReduxState = JSON.stringify(getState());

  return {
    props: {
      initialReduxState,
      showTopBanner,
      isDesktop: device.isDesktop(),
      themeKey,
    },
  };
};

export default PreAuthWebsitePdpContainer;
